import { useMemo } from 'react';
import { MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { Accordion, AccordionItem } from '@ds';
import { SelectCasesType } from '@lp/features/MultiSuggest/types';

const RULES_TITLE_MAP: Record<SelectCasesType, string> = {
  [SelectCasesType.Initial]: '{{string}} available for selection as an initial case for multi-case suggestion',
  [SelectCasesType.Target]: '{{string}} available for selection as target cases for multi-case suggestion',
};

type ListRulesProps = {
  type: SelectCasesType;
  casesCount: number;
};

const InitialCasesRules = () => (
  <div className="flex gap-8 p-4 bg-base-90 rounded-2xl">
    <div className="flex-1 flex flex-col gap-2">
      <h4 className="text-l-sb text-base-5">Included</h4>
      <div className="text-m-r text-base-10 flex flex-col gap-1">
        <p>
          <span className="text-m-sb">Case type:</span> Regular or trial.
        </p>
        <p>
          <span className="text-m-sb">Case status:</span> Pending Logistics, Resources Assigned.
        </p>
        <p>
          <span className="text-m-sb">Donor OR:</span> (now + 3h) &lt; OR &lt; (now + 3h + 24h).
        </p>
        <div>
          <span className="text-m-sb">If the case has route:</span>
          <ul className="list-disc pl-5 space-y-1">
            <li>Time to first leg departure: &gt; 3 hours;</li>
            <li>Route type: Main Route;</li>
            <li>Transportation type: Air &amp; Ground;</li>
            <li>Air logistics provider: TMDX Jet;</li>
            <li>
              Resources (Clinical Specialist, Retrieval Surgeon and Device) not yet deployed:
              <ul className="list-none pl-1 mt-1 space-y-1">
                <li>Reserved</li>
                <li>Acknowledgment pending</li>
                <li>Acknowledged/Assigned</li>
                <li>
                  Note: the Retrieval Surgeon may be absent on the case if the NOP Service = ‘NOP Service without TMDX
                  Surgeon’.
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div className="flex-1 flex flex-col gap-2">
      <h4 className="text-l-sb text-base-5">Excluded</h4>
      <div className="text-m-r text-base-10">
        <ul className="list-disc pl-5 space-y-1">
          <li>Re-allocation &amp; OPO cases (when no TX Center is available).</li>
          <li>European cases.</li>
          <li>DA cases.</li>
          <li>Ground only cases.</li>
          <li>Cases using only TX Center &amp; Supplemental Lift logistics providers and not using any TMDX Jet.</li>
        </ul>
      </div>
    </div>
  </div>
);

const TargetCasesRules = () => (
  <div className="flex gap-8 p-4 bg-base-90 rounded-2xl">
    <div className="flex-1 flex flex-col gap-2">
      <h4 className="text-l-sb text-base-5">Included</h4>
      <div className="text-m-r text-base-10 flex flex-col gap-1">
        <p>
          <span className="text-m-sb">Case type:</span> Regular or trial.
        </p>
        <p>
          <span className="text-m-sb">Case status:</span> Resources Assigned, Pending Logistics (Logistics status: Route
          Planning Done, Acknowledgment pending &amp; Resources Assigned).
        </p>
        <p>
          <span className="text-m-sb">Donor OR:</span> (now + 3h) &lt; OR &lt; (now + 3h + 24h).
        </p>
        <div>
          <span>
            The case has <span className="font-bold">route</span> and:
          </span>
          <ul className="list-disc pl-5 space-y-1">
            <li>Time to first leg departure: &gt; 3 hours</li>
            <li>Route type: Main Route</li>
            <li>Transportation type: Air &amp; Ground.</li>
            <li>Air logistics provider: TMDX Jet</li>
            <li>
              Resources: (Clinical Specialist, Retrieval Surgeon and Device) not yet deployed:
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>Reserved</li>
                <li>Acknowledgment pending</li>
                <li>Acknowledged/Assigned</li>
                <li>
                  Note: the Retrieval Surgeon may be absent on the case if the NOP Service = ‘NOP Service without TMDX
                  Surgeon’.
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div className="flex-1 flex flex-col gap-2">
      <h4 className="text-l-sb text-base-5">Excluded</h4>
      <div className="text-m-r text-base-10">
        <ul className="list-disc pl-5 space-y-1">
          <li>Initial (origin) case.</li>
          <li>Ground only cases.</li>
          <li>Cases using only TX Center &amp; Supplemental Lift logistics providers and not using any TMDX Jet.</li>
          <li>Re-allocation &amp; OPO cases (when no TX Center is available).</li>
          <li>European cases.</li>
          <li>DA cases.</li>
        </ul>
      </div>
    </div>
  </div>
);

export const ListRules = ({ type, casesCount }: ListRulesProps) => {
  const title = useMemo(() => {
    const startString = casesCount === 1 ? 'case is' : 'cases are';

    return RULES_TITLE_MAP[type].replace('{{string}}', `${casesCount} ${startString}`);
  }, [type, casesCount]);

  return (
    <Accordion className="p-0 w-auto!" isCompact showDivider={false}>
      <AccordionItem
        classNames={{
          heading: 'w-fit',
          trigger: 'cursor-pointer p-0',
          title: 'text-m-m! text-base-10',
          indicator: 'text-black data-[open=true]:-rotate-180',
        }}
        title={title}
        indicator={<MdOutlineKeyboardArrowDown size={24} />}
      >
        {type === SelectCasesType.Initial && <InitialCasesRules />}
        {type === SelectCasesType.Target && <TargetCasesRules />}
      </AccordionItem>
    </Accordion>
  );
};
